import type { AuthenticationResult, EventMessage, IdTokenClaims } from "@azure/msal-browser";
import * as msal from "@azure/msal-browser";
import { NavigationClient, EventType } from "@azure/msal-browser";
import * as R from "ramda";

import { artesianTokenErrorModalRef } from "../../../components/artesianTokenErrorModalRef";
import type { CustomSettingsType } from "../../../config/global";
import { getTenantsListFromTokenClaims } from "../../helper";
import { router } from "../../router";
import type { UserAccountInfo } from "../authTypes";
import { LoginStatus } from "../authTypes";

import type { AuthProvider } from "./authProviderInterface";

const log = {
  warn: (...args: unknown[]) => {
    if (process.env.NODE_ENV !== "production") console.warn(...args);
  },
  error: (...args: unknown[]) => {
    console.error(...args);
  },
};

export type MSALConfig = {
  msalConfig: msal.Configuration;
  scopes: string[];
  extraQueryParameters?: Record<string, string>;
};

export type MsalAuthProviderConfig = {
  scopes: string;
  clientID: string;
  authority: string;
  knownAuthorities: string;
  redirectUri: string;
  silentRedirectUri: string;
  permissionsClaims: string[];
  authConnection?: string;
};

class CustomNavigationClient extends NavigationClient {
  async navigateInternal(url: string, options: msal.NavigationOptions) {
    const relativePath = url.replace(window.location.origin, "");
    await router.navigate(relativePath, { replace: options.noHistory });
    return false;
  }
}

export class MsalAuthProvider implements AuthProvider {
  private config: MSALConfig;
  private myMSALObj: msal.IPublicClientApplication;
  private loginStatus: LoginStatus = LoginStatus.NotLogged;
  private loginPopupRequest: msal.PopupRequest;
  private idTokenClaims: msal.IdTokenClaims | null = null;
  private subscribers = new Set<(status: LoginStatus) => void>();

  constructor(env: CustomSettingsType) {
    const scopes = env.scopes.split(",");
    const baseMsalConfig: msal.Configuration = {
      auth: {
        clientId: env.clientID,
        authority: env.authority,
        knownAuthorities: env.knownAuthorities.split(","),
        redirectUri: env.redirectUri,
        postLogoutRedirectUri: window.origin,
        navigateToLoginRequestUrl: true,
      },
      cache: {
        cacheLocation: "localStorage",
        storeAuthStateInCookie: false,
      },
      system: {
        allowRedirectInIframe: false,
        loggerOptions: {
          loggerCallback: (level: msal.LogLevel, message: string, containsPii: boolean): void => {
            if (containsPii) return;
            switch (level) {
              case msal.LogLevel.Error:
                console.error(message);
                return;
              case msal.LogLevel.Info:
                console.info(message);
                return;
              case msal.LogLevel.Verbose:
                console.debug(message);
                return;
              case msal.LogLevel.Warning:
                console.warn(message);
                return;
            }
          },
          piiLoggingEnabled: false,
        },
        windowHashTimeout: 20000,
        iframeHashTimeout: 20000,
      },
    };

    const loginRequest =
      env.authConnection === "K4View"
        ? {
            scopes: ["openid", "offline_access", ...scopes],
            extraQueryParameters: { domain_hint: "k4view" },
          }
        : {
            scopes: ["openid", "offline_access", ...scopes],
          };

    this.config = {
      msalConfig: baseMsalConfig,
      scopes,
      ...(loginRequest.extraQueryParameters && { extraQueryParameters: loginRequest.extraQueryParameters }),
    };

    this.loginPopupRequest = loginRequest;

    this.myMSALObj = new msal.PublicClientApplication(this.config.msalConfig);
    this.myMSALObj.setNavigationClient(new CustomNavigationClient());
    this.myMSALObj.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        const account = payload.account;
        this.myMSALObj.setActiveAccount(account);
        this.idTokenClaims = payload.idTokenClaims;
        this.loginStatus = LoginStatus.Logged;
        this.notifySubscribers();
      }
      if (event.eventType === EventType.LOGIN_FAILURE && event.payload) {
        this.idTokenClaims = null;
        this.loginStatus = LoginStatus.Error;
        this.notifySubscribers();
      }
    });
  }

  private saveTokenExpiry(result: AuthenticationResult) {
    const expiresOn = result.expiresOn?.toISOString() ?? "unknown";
    if (expiresOn && result.expiresOn) {
      const expiryTime = typeof result.expiresOn === "number" ? result.expiresOn : result.expiresOn.getTime();

      localStorage.setItem("arkTokenExpiry", expiryTime.toString());
      localStorage.setItem("arkTokenExpiryReadable", expiresOn);
    }
  }

  private notifySubscribers() {
    for (const subscriber of this.subscribers) {
      subscriber(this.loginStatus);
    }
  }

  private getUserPermissions(): string[] {
    if (this.idTokenClaims) {
      const permissions = R.pathOr("", ["extension_permissions"], this.idTokenClaims).split(" ");
      return permissions;
    }
    return [];
  }
  public async init(): Promise<void> {
    await this.myMSALObj.initialize();

    const response = await this.myMSALObj.handleRedirectPromise();

    if (response?.account) {
      this.myMSALObj.setActiveAccount(response.account);
      this.idTokenClaims = response.idTokenClaims;
    }

    const accounts = this.myMSALObj.getAllAccounts();
    if (accounts.length > 0 && !this.myMSALObj.getActiveAccount()) {
      this.myMSALObj.setActiveAccount(accounts[0]);
    }

    const active = this.myMSALObj.getActiveAccount();
    if (!active) {
      log.warn("🚫 No account found after redirect — triggering loginRedirect...");
      await this.login();
      return;
    }

    const idp = active.idTokenClaims?.idp;
    if (idp === "live.com") {
      log.warn("⚠️ Microsoft Account detected — silent refresh may fail after 1 hour");
    }

    try {
      const silentResult = await this.myMSALObj.acquireTokenSilent({
        account: active,
        scopes: this.config.scopes,
        ...(this.config.extraQueryParameters && { extraQueryParameters: this.config.extraQueryParameters }),
      });

      this.idTokenClaims = silentResult.idTokenClaims;
      this.loginStatus = LoginStatus.Logged;
      this.notifySubscribers();

      this.saveTokenExpiry(silentResult);

      log.warn("✅ Token validated silently on init");
    } catch (e: unknown) {
      log.warn("🔁 Silent init validation failed:", e);

      if (e instanceof msal.InteractionRequiredAuthError || e instanceof msal.BrowserAuthError) {
        log.warn("🔁 MSAL Error Code:", e.errorCode);
        log.warn("🔁 SubError:", e.subError);
        log.warn("🔁 Redirecting to login...");

        await this.login();
        return;
      } else {
        log.error("❌ Unexpected silent token error during init", e);
      }
    }
  }

  public async login(): Promise<void> {
    await this.myMSALObj.loginRedirect(this.loginPopupRequest);
  }

  public async loginPopup(): Promise<void> {
    const result = await this.myMSALObj.loginPopup(this.loginPopupRequest);

    if (result?.expiresOn) {
      const expiresOn = result.expiresOn.toISOString();
      const expiryTime = result.expiresOn.getTime();

      localStorage.setItem("arkTokenExpiry", expiryTime.toString());
      localStorage.setItem("arkTokenExpiryReadable", expiresOn);
    }
  }

  public async logout() {
    const itemKey = "msal.interaction.status";
    if (localStorage.getItem(itemKey)) {
      localStorage.removeItem(itemKey);
    }
    await this.myMSALObj.logoutRedirect({
      account: this.myMSALObj.getActiveAccount(),
    });
  }

  public async getToken(): Promise<string | null> {
    const account = this.myMSALObj.getActiveAccount() ?? this.myMSALObj.getAllAccounts()[0];

    if (!account) {
      log.warn("⚠️ No active account found - initiating login");
      await this.login();
      return null;
    }

    this.myMSALObj.setActiveAccount(account);

    try {
      const result = await this.myMSALObj.acquireTokenSilent({
        account,
        scopes: this.config.scopes,
        ...(this.config.extraQueryParameters && { extraQueryParameters: this.config.extraQueryParameters }),
      });

      this.saveTokenExpiry(result);
      this.loginStatus = LoginStatus.Logged;
      this.notifySubscribers();

      return result.accessToken;
    } catch (error) {
      log.warn("🔁 Silent token acquisition failed:", error);

      if (error instanceof msal.InteractionRequiredAuthError) {
        log.warn("🔁 Token expired or interaction required - redirecting to login");
        this.loginStatus = LoginStatus.NotLogged;
        this.notifySubscribers();
        await this.login();
        return null;
      } else if (error instanceof msal.BrowserAuthError) {
        log.error("🔁 Browser auth error:", error.errorCode, error.subError);
        this.loginStatus = LoginStatus.Error;
        this.notifySubscribers();
        // For certain errors, try login again
        if (error.errorCode === "no_tokens_found" || error.errorCode === "user_login_error") {
          await this.login();
        }
        return null;
      } else {
        log.error("❌ Unexpected token acquisition error:", error);
        this.loginStatus = LoginStatus.Error;
        this.notifySubscribers();
        // Show the Artesian token error modal for unexpected errors
        if (artesianTokenErrorModalRef.show) {
          artesianTokenErrorModalRef.show();
        }
        return null;
      }
    }
  }

  public getLoginStatus(): LoginStatus {
    return this.loginStatus;
  }

  public onLoginStatus(subscriber: (status: LoginStatus) => void) {
    this.subscribers.add(subscriber);
    return () => {
      this.subscribers.delete(subscriber);
    };
  }

  public async handleLoginRedirect(): Promise<void> {
    const interactionStatus = sessionStorage.getItem("msal.interaction.status");
    if (interactionStatus === "interaction_in_progress") {
      log.warn("🛑 Redirect already in progress — skipping");
      return;
    }
    await this.myMSALObj.handleRedirectPromise();
  }

  public async getUserDetail(): Promise<UserAccountInfo | null> {
    const account = this.myMSALObj.getActiveAccount();
    if (!account) {
      log.warn("[LOG] No active account - checking for existing accounts");
      const accounts = this.myMSALObj.getAllAccounts();

      if (accounts.length === 0) {
        log.warn("[LOG] No accounts found - initiating login");
        this.loginStatus = LoginStatus.NotLogged;
        this.notifySubscribers();
        await this.login();
        return null;
      } else {
        this.myMSALObj.setActiveAccount(accounts[0]);
      }
    }

    const activeAccount = this.myMSALObj.getActiveAccount();
    if (!activeAccount) {
      return null;
    }

    try {
      const resp = await this.myMSALObj.acquireTokenSilent({
        account: activeAccount,
        scopes: this.config.scopes,
        ...(this.config.extraQueryParameters && { extraQueryParameters: this.config.extraQueryParameters }),
      });

      this.idTokenClaims = resp.idTokenClaims;
      this.loginStatus = LoginStatus.Logged;
      this.notifySubscribers();
      this.saveTokenExpiry(resp);

      return {
        username: activeAccount.idTokenClaims?.name,
        email: activeAccount.idTokenClaims?.emails,
        permissions: this.getUserPermissions(),
        idTokenClaims: activeAccount.idTokenClaims,
        tenantId: activeAccount.tenantId,
        tenants: getTenantsListFromTokenClaims(activeAccount.idTokenClaims as IdTokenClaims),
      } as UserAccountInfo;
    } catch (e) {
      log.error("[LOG] Error getting user details:", e);

      if (e instanceof msal.InteractionRequiredAuthError) {
        log.warn("[LOG] Token expired - redirecting to login");
        this.loginStatus = LoginStatus.NotLogged;
        this.notifySubscribers();
        await this.login();
      } else if (e instanceof msal.BrowserAuthError) {
        log.error("[LOG] Browser auth error:", e.errorCode, e.subError);
        this.loginStatus = LoginStatus.Error;
        this.notifySubscribers();
        // Try login for recoverable errors
        if (e.errorCode === "no_tokens_found" || e.errorCode === "user_login_error") {
          await this.login();
        }
      } else {
        this.loginStatus = LoginStatus.Error;
        this.notifySubscribers();
      }

      return null;
    }
  }

  public hasPermission(permission: string) {
    const permissions = this.getUserPermissions();
    return permissions.includes(permission);
  }
}
